"use client";

import { useState } from "react";
import Link from "next/link";
import { useQueryClient } from "@tanstack/react-query";
import { Avatar } from "./ui/Avatar";
import { ProfileHoverCard } from "./ProfileHoverCard";
import { profileHref } from "@/lib/profileHref";
import { AffiliationChip } from "./AffiliationChip";
import { HiddenChip } from "./HiddenChip";
import { BotBadge } from "./BotBadge";
import { BotTranscriptButton } from "./BotTranscriptButton";
import { TimeAgo, EditedLabel } from "./TimeAgo";
import { EmojiReactionPicker } from "./EmojiReactionPicker";
import { ActionMenu, ActionMenuItem } from "@/components/ui/ActionMenu";
import { ReactionSummary } from "./ReactionSummary";
import { Emoji } from "@/components/ui/Emoji";
import { CommentComposer } from "./CommentComposer";
import { FlagRuleSelect } from "./FlagRuleSelect";
import { RemoveContentDialog } from "./RemoveContentDialog";
import { CommentEditor } from "./CommentEditor";
import { usePostReaction } from "@/lib/hooks/usePostReaction";
import { commentsKey } from "@/lib/hooks/useComments";
import { dropLocalComment } from "@/lib/thread";
import { enterThread } from "@/lib/hooks/useThreadFocus";
import { groupPostsPrefix } from "@/lib/hooks/useGroupPage";
import { homeFeedKey } from "@/lib/hooks/useHomeFeed";
import { useContentModeration } from "@/lib/hooks/useContentModeration";
import { useRemoveFailedVideo } from "@/lib/hooks/useRemoveFailedVideo";
import { RichContent } from "./RichContent";
import { ImageLightbox } from "./ImageLightbox";
import { CommentLightboxSidebar } from "./CommentLightboxSidebar";
import { imageAttachments, readyAttachments, videoAttachments } from "@/lib/attachments";
import { AttachedFiles } from "./AttachedFiles";
import type { Comment, PostComment } from "@theta/api-client";

/**
 * A row in a thread.
 *
 * TWO SHAPES, ONE COMPONENT. `GET /posts/{id}/comments` answers `Comment`; a
 * feed card embeds `PostComment`, the bounded preview, which differs by
 * `depth` and `state` — the preview carries `isHidden` too since OSO-582,
 * because a feed can now contain a held comment. Both carry `can` — the
 * preview did not until OSO-538, and every affordance below being `can` meant
 * the whole row went inert in a feed rather than degrading (D11 is only
 * satisfied if the server actually answers).
 *
 * `state` IS THE THREAD'S ALONE (OSO-583) and the `"state" in comment` guard
 * below says so: only a thread keeps a deleted comment as a tombstone, because
 * only a thread has a shape to preserve. A feed preview re-parents the replies
 * to the nearest ancestor it can still show and drops the row, which is what
 * it has always done.
 */
export type ThreadComment = Comment | PostComment;

export function CommentItem({
  comment,
  postId,
  groupId,
  groupSlug,
  currentUserId,
  replies,
  pinnedReplies,
  nested = false,
  defaultExpanded = false,
  forceExpandReplies = false,
  unreadIds,
}: {
  comment: ThreadComment;
  /** The post the thread hangs off — what a new reply is invalidated against. */
  postId: number;
  groupId?: number;
  /** The group's slug, for the @alle capability lookup (OSO-681). */
  groupSlug?: string;
  currentUserId?: number | null;
  replies?: ThreadComment[];
  pinnedReplies?: ThreadComment[];
  /**
   * Which comment ids are new-to-this-viewer (OSO-622) — the frozen per-mount
   * set from `useUnreadSnapshot`, present only on home-feed cards that opted
   * in. A SET, not a boolean, because it has to travel through the recursive
   * render sites below: an unread nested reply lights up while its read
   * parent, rendered as context by the ancestor-closed preview, does not.
   */
  unreadIds?: ReadonlySet<number>;
  /**
   * True when this row is rendered inside a branch's reply list. Its own
   * subtree is already flattened into that list, so it must never offer a
   * "svar" toggle — the on-demand fetch would render its children a second
   * time, beside themselves, with duplicate `#kommentar-{id}` anchors.
   */
  nested?: boolean;
  defaultExpanded?: boolean;
  // Forces the (already-fetched) replies open even after mount — used to reveal
  // a reply targeted by a "#kommentar-N" deep link that was collapsed by default.
  forceExpandReplies?: boolean;
}) {
  const { currentReaction, currentReactionUrl, counts, handleReact, handleQuickLike } = usePostReaction(
    comment.id,
    comment.reactions
  );
  const queryClient = useQueryClient();
  const [showReply, setShowReply] = useState(false);
  /**
   * Expanded when the thread supplied the replies inline, or when a deep link
   * points into this branch.
   *
   * `forceExpandReplies` IS IN THE INITIAL VALUE, not only in the sync below —
   * OSO-615. On a post's own page the thread arrives AFTER the card mounts
   * (`SinglePostClient` renders with `comments: []`), so the branch list, and
   * with it this component, is built once the target is already known: the prop
   * is `true` from the first render and never changes. A sync that fires only
   * on a change therefore did nothing, the reply stayed folded behind "1 svar",
   * and the scroll had nothing to find.
   */
  const [showReplies, setShowReplies] = useState(
    (defaultExpanded && replies !== undefined) || forceExpandReplies,
  );
  const [showMenu, setShowMenu] = useState(false);
  const [localBodyHtml, setLocalBodyHtml] = useState<string | null>(null);
  const [deleted, setDeleted] = useState(false);
  /**
   * A REMOVED COMMENT LEAVES THE THREAD, NOT JUST THIS COMPONENT (OSO-563).
   *
   * Hiding the row locally is not enough now that the thread is a cache: on a
   * feed card `invalidateOnRemove`'s `commentsKey` is a no-op — that query is
   * disabled — so the row survived in the cache, and if it was one the viewer
   * had just written, `mergeSeed` protected it from every later snapshot as
   * well. Navigating away and back inside `gcTime` brought the deleted comment
   * back, and nothing ever took it away again.
   */
  const dropFromThread = () => {
    setDeleted(true);
    dropLocalComment(postId, comment.id);
    queryClient.setQueryData<ThreadComment[]>(commentsKey(postId), (thread) =>
      thread?.filter((row) => row.id !== comment.id),
    );
  };
  /**
   * DELETING A COMMENT THAT HAS REPLIES NO LONGER REMOVES IT (OSO-583).
   *
   * The server answers the next read with a tombstone in its place, so taking
   * the row off the screen here would take its replies with it until that read
   * lands and then put them back — the flicker the change exists to remove.
   * The LEDGER still forgets it either way: a locally-written row that has
   * since been deleted must never be re-injected as a live one (OSO-563), and
   * a tombstone arrives from the server under the same id.
   *
   * The test is deliberately generous — a `replyCount` the walk knows about
   * but this page has not reached yet still counts — because being wrong
   * towards "let the server say" costs one refetch, and being wrong the other
   * way empties a branch the reader was looking at.
   *
   * Hiding still drops unconditionally: a hidden comment DOES prune its
   * subtree, deliberately, so that the replies do not reproduce what was
   * withheld (see `threadPage`'s header).
   */
  const afterDelete = () => {
    dropLocalComment(postId, comment.id);
    if ((replies?.length ?? 0) > 0 || comment.replyCount > 0) {
      queryClient.invalidateQueries({ queryKey: commentsKey(postId) });
      return;
    }
    dropFromThread();
  };

  /**
   * EVERY AFFORDANCE BELOW IS `can`, from the same predicate the route
   * enforces. There is no `userRole` prop any more and no
   * `role === "admin" || role === "moderator"` anywhere: D11 removed the role
   * from the wire precisely because recomputing a capability from it was its
   * only use, and a client's copy of the rule is the copy that goes stale.
   */
  const {
    edit: canEdit,
    delete: canDelete,
    hide: canHide,
    unhide: canUnhide,
    flag: canFlag,
    react: canReact,
    reply: canReply,
  } = comment.can;
  const hasMenu = canEdit || canDelete || canHide || canUnhide;

  /**
   * THE PROFILE ADDRESS IS THE ID (ruling O-21a): `/profil/{id}`, never
   * `/profil/{username}` — ten real accounts carry a numeric username equal to
   * a DIFFERENT user's id, so the old route served the wrong person for them.
   * Null when the comment has no attributable author: anonymous, or an account
   * that has since been deleted.
   */
  const authorId = comment.author?.id ?? null;
  const authorHref = profileHref(authorId);
  const authorName = comment.author?.name ?? "Anonym";
  /**
   * Whether the server DE-ANONYMISED this comment for us.
   *
   * This used to be `comment.username !== "anonym"` — an anonymity guarantee
   * resting on a string comparison against a sentinel (O-21 names this exact
   * line). On /api/v1 an anonymous author is simply `null` for anybody who may
   * not see through it, and a populated `author` on an anonymous comment IS
   * the server saying "you may". Nothing is inferred from a magic word.
   */
  const deanonymised = comment.isAnonymous && comment.author !== null;
  const hiddenAnonymous = comment.isAnonymous && comment.author === null;

  /**
   * WHO Svar TAGS FOR YOU (OSO-596) — nobody, unless there is a name that may
   * be written into a public body.
   *
   * NEVER AN ANONYMOUS COMMENT, and `deanonymised` is exactly why the test is
   * `isAnonymous` and not `author == null`: a moderator who may see through
   * the anonymity would otherwise be handed a tag that publishes the name to
   * everybody. Not a deleted account either (no id), and not yourself — the
   * producer drops a self-mention anyway, so it would only be noise.
   */
  const replyTag =
    !comment.isAnonymous && authorId != null && authorId !== currentUserId
      ? { id: authorId, name: authorName }
      : null;

  const {
    isEditing,
    setIsEditing,
    editText,
    editAttachments,
    editLoading,
    startEdit,
    remove,
    hide,
    unhide,
    removal,
    removalRuleId,
    setRemovalRuleId,
    removalPending,
    confirmRemoval,
    cancelRemoval,
    showFlagForm,
    setShowFlagForm,
    flagReason,
    setFlagReason,
    flagRules,
    rulesLoading,
    flagRuleId,
    setFlagRuleId,
    flagLoading,
    flagDone,
    flag,
    actionError,
  } = useContentModeration({
    postId: comment.id,
    groupId,
    subject: "Kommentaren",
    invalidateOnRemove: [commentsKey(postId), groupPostsPrefix, homeFeedKey],
    onDeleted: () => afterDelete(),
    onHidden: () => dropFromThread(),
  });

  /** See `PostCard`'s `moderatingRemoval` — the same line, on the comment's
   *  `can`, including why it is not a comparison against `comment.author`. */
  const moderatingRemoval = (canHide || canUnhide) && !canEdit;

  /**
   * FADE THE MENU BUTTON IN ON HOVER — but only where there is a hover to wait
   * for. `lg:` was standing in for "desktop", and a tablet held in landscape is
   * `lg` with a coarse pointer that never hovers, so the button sat at
   * `opacity-0` with nothing on the device able to reveal it. `pointer-fine:`
   * asks the question the styling actually depends on. `focus-within` is the
   * keyboard's way in, and an open menu stays lit whatever the pointer has done
   * since.
   */
  const menuRevealClass = showMenu
    ? ""
    : "pointer-fine:lg:opacity-0 pointer-fine:lg:group-hover/comment:opacity-100 pointer-fine:lg:focus-within:opacity-100";

  const handleStartEdit = () => {
    setShowMenu(false);
    startEdit();
  };
  const handleDelete = () => {
    setShowMenu(false);
    remove();
  };
  const handleHide = () => {
    setShowMenu(false);
    hide();
  };
  const handleUnhide = () => {
    setShowMenu(false);
    unhide();
  };

  /**
   * THE REPLIES ARE THE ONES THE THREAD ALREADY HAS (OSO-563).
   *
   * There was a second read here — `?depth=1` on this comment's own id —
   * completing a branch the feed's bounded preview had truncated. It is gone
   * because a branch is now completed by READING THE THREAD instead: expanding
   * one enters it (`enterThread`), the thread goes live, and
   * `GET /posts/{id}/comments?depth=all` brings every reply rather than this
   * comment's alone. One read, one shape, one cache — and a reply written here
   * lands in it, which the `?depth=1` query could not do: it was disabled
   * whenever the preview happened to carry the whole branch, so invalidating
   * it after a write was frequently a no-op. That was half of why a reply
   * written in the feed did not appear.
   *
   * `replyCount` below is still the wire's, which counts replies this list may
   * not carry — so the count is honest before the live read lands, and the
   * rows follow a moment later.
   */

  // `defaultExpanded` only seeds the initial state at mount, but a deep-link
  // hash can resolve to this comment's root *after* it already mounted.
  // Adjust state during render (React's recommended escape hatch for this)
  // instead of an effect, so it takes effect in the same commit rather than
  // one render late.
  const [prevForceExpandReplies, setPrevForceExpandReplies] = useState(forceExpandReplies);
  if (forceExpandReplies !== prevForceExpandReplies) {
    setPrevForceExpandReplies(forceExpandReplies);
    if (forceExpandReplies && !showReplies) setShowReplies(true);
  }

  // ONE `attachments` ARRAY (C16/U11). The gallery reads the images out of it
  // rather than a separate `image_urls`, so the two can no longer disagree.
  // Images: ready ones only. Videos: ALL of them, because a clip that is still
  // encoding is exactly what the player draws a placeholder for — see the same
  // two lines in `PostCard` (OSO-490).
  const images = readyAttachments(imageAttachments(comment.attachments));
  // Videos get their own player rather than a gallery cell, and it never
  // starts on its own (OSO-437).
  const videos = videoAttachments(comment.attachments);

  /**
   * NOT IN THE THREAD FOR ANYBODY ELSE YET (OSO-490 §5). Its author sees it
   * inert — nobody can have reacted to a comment nobody has read, and the
   * root's `replyCount` does not count it either.
   *
   * DIMMED ONLY WHILE IT IS A WAIT. See the same three lines in `PostCard`: a
   * failed transcode is not "come back later", it is "this will not publish
   * until you act", and the card must neither say the first nor spend its
   * contrast dimming the control that ends it.
   */
  const pending = comment.publishedAt === null;
  const blockedByFailure =
    pending && comment.attachments.some((a) => a.status === "failed");
  const awaitingVideo = pending && !blockedByFailure;
  /** Held for review or hidden by a moderator — see `PostCard`'s note (OSO-582). */
  const muted = awaitingVideo || comment.isHidden;
  /**
   * A TOMBSTONE — OSO-583. The server kept this row because replies to it
   * survived, and stripped everything else: no author, no body, no
   * attachments, no reactions, and every `can` false. So the branches below
   * that read those fields render nothing on their own; what this flag decides
   * is the three that would otherwise render a PLACEHOLDER — the avatar falls
   * back to initials, the name falls back to "Anonym", and the body would be
   * an empty bubble. One sentence replaces all three.
   */
  const removed = "state" in comment && comment.state === "removed";

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const hasReactions = counts.some((c) => c.count > 0);

  // The list can be one row AHEAD of the wire's count while a reply of the
  // viewer's own is still in flight, so the larger of the two is the honest
  // one. `PostCard` counts the whole thread the same way.
  const replyCount = nested ? 0 : Math.max(replies?.length ?? 0, comment.replyCount);
  const displayReplies = replies;
  const bodyHtml = localBodyHtml ?? comment.bodyHtml;

  /**
   * "Fjern video" on a comment is the same edit as on a post — a comment IS a
   * post row, at the same `/posts/{id}` (OSO-490 §2).
   *
   * BUT A COMMENT CAN BE THE VIDEO AND NOTHING ELSE. `CommentComposer` submits
   * on "text OR attachments", so an attachment-only comment is a shape it
   * produces on purpose; take its one failed clip away and the edit composes
   * to an empty body with no attachments, which `PATCH /posts/{id}` refuses
   * with `bodyEmpty`. That is the one row where the way out is the DELETE the
   * menu already has, and `emptiesTheRow` is what tells them apart. `bodyHtml`
   * and not `comment.bodyHtml`: a just-saved edit is the current source.
   */
  const { removeFailed, removing, removeError, emptiesTheRow } =
    useRemoveFailedVideo({
      postId: comment.id,
      bodyHtml,
      attachments: comment.attachments,
      invalidate: [commentsKey(postId), groupPostsPrefix, homeFeedKey],
    });

  const pinnedReplyIds = new Set((pinnedReplies ?? []).map((r) => r.id));
  const remainingReplies = displayReplies?.filter((r) => !pinnedReplyIds.has(r.id));

  /**
   * THE BUBBLE IS WHAT HIGHLIGHTS, NOT THE ROW (OSO-622): the bubble is the
   * comment's own surface, and tinting the whole row would swallow the reply
   * strip and the nested replies under it. `bg-primary-muted` is the
   * theme-aware pair mentions already use (indigo-50 light / indigo-950
   * dark); the body's foreground colours pass on both, so the text is left
   * alone. Distinct from `.comment-highlight`, the deep-link FLASH — that is
   * an animation answering "where did the link land", this is a state
   * answering "what is new since last visit".
   */
  const unread = unreadIds?.has(comment.id) ?? false;

  if (deleted) return null;

  return (
    <div
      id={`kommentar-${comment.id}`}
      className={`scroll-mt-20${muted ? " opacity-60" : ""}`}
      data-pending={pending || undefined}
      data-pending-reason={pending ? (blockedByFailure ? "failed" : "processing") : undefined}
    >
      <div className="flex gap-2 items-start group/comment">
        {removed ? null : hiddenAnonymous ? (
          <div className="shrink-0 w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-500 dark:text-purple-400">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="23" y1="11" x2="17" y2="11" />
            </svg>
          </div>
        ) : (
          <ProfileHoverCard userId={authorId} categoryId={groupId}>
            {authorHref ? (
              <Link href={authorHref} className="shrink-0">
                <Avatar url={comment.author?.avatar.medium ?? ""} name={authorName} size="sm" />
              </Link>
            ) : (
              <span className="shrink-0">
                <Avatar url={comment.author?.avatar.medium ?? ""} name={authorName} size="sm" />
              </span>
            )}
          </ProfileHoverCard>
        )}
        <div className="flex-1 min-w-0">
          <div className={`relative inline-block max-w-full has-[.youtube-embed]:block ${hasReactions ? "mb-2" : ""}`}>
            <div className={`${unread ? "bg-primary-muted" : "bg-muted"} rounded-2xl px-2.5 pt-2 sm:px-3 ${hasReactions ? "pb-4" : "pb-2"}`}>
              {/* OSO-585 — the "..." button hangs over this row's right edge, so
                  the author line is the one that reserves width for it. The body
                  below it runs the bubble's full width. */}
              <div className={hasMenu && !isEditing ? "pr-5" : ""}>
                {removed ? null : comment.isAnonymous ? (
                  <span className="font-semibold text-xs text-purple-700 dark:text-purple-300">
                    Anonym
                    {deanonymised && (
                      <span className="ml-1 text-subtle-foreground font-normal" title="Kun synlig for dig">
                        ({authorName})
                      </span>
                    )}
                  </span>
                ) : (
                  <>
                    <ProfileHoverCard userId={authorId} categoryId={groupId}>
                      {authorHref ? (
                        <Link href={authorHref} className="font-semibold text-xs text-foreground hover:underline">
                          {authorName}
                        </Link>
                      ) : (
                        <span className="font-semibold text-xs text-foreground">{authorName}</span>
                      )}
                    </ProfileHoverCard>
                    {comment.author?.isBot && (
                      <>
                        <BotBadge className="ml-1.5 align-middle" />
                        <BotTranscriptButton
                          postId={comment.id}
                          className="ml-1.5 align-middle inline-flex"
                        />
                      </>
                    )}
                    {comment.authorAffiliation && (
                      <AffiliationChip
                        affiliation={comment.authorAffiliation}
                        inherited
                        className="ml-1.5 align-middle"
                      />
                    )}
                  </>
                )}
              </div>
              {removed ? (
                /* OSO-583. One line, in the muted colour and italic, so the
                   row reads as a placeholder rather than as somebody's very
                   short comment. `bg-muted` still draws the bubble, because
                   what the tombstone is FOR is holding the shape of the
                   thread — an unstyled line would not look like a place. */
                <p className="text-content italic text-subtle-foreground my-0.5">
                  Denne kommentar er blevet slettet
                </p>
              ) : isEditing ? (
                <CommentEditor
                  commentId={comment.id}
                  groupSlug={groupSlug}
                  initialText={editText}
                  initialAttachments={editAttachments}
                  groupId={groupId}
                  onCancel={() => setIsEditing(false)}
                  onSaved={(updated) => {
                    setLocalBodyHtml(updated.bodyHtml);
                    setIsEditing(false);
                    queryClient.invalidateQueries({ queryKey: commentsKey(postId) });
                  }}
                />
              ) : (
                /* Body, link previews, YouTube, audio and the bounded
                   image gallery — all at the comment density. */
                <RichContent
                  density="comment"
                  /* Everywhere, unlike a post (OSO-606): a comment has no page
                     of its own to be read in full on. */
                  collapsible
                  html={bodyHtml}
                  // OSO-568 — a comment's dismissal is the post's rule.
                  dismissedLinkPreviewUrl={comment.dismissedLinkPreviewUrl}
                  images={images.map((a) => a.url)}
                  gridImages={images.map((a) => a.renditions?.grid ?? a.url)}
                  videos={videos.map((a) => ({
                    url: a.url,
                    hlsUrl: a.hlsUrl,
                    filename: a.filename,
                    posterUrl: a.posterUrl,
                    width: a.width,
                    height: a.height,
                    status: a.status,
                    // `canEdit` gates the edit; the empty-row case issues a
                    // DELETE instead, so it is gated on `canDelete` — the
                    // server's own answer, not on `edit` implying it (it
                    // does today: `delete: isAuthor || canModerate`).
                    onRemove: emptiesTheRow
                      ? canDelete
                        ? handleDelete
                        : undefined
                      : canEdit
                        ? removeFailed
                        : undefined,
                    removeLabel: emptiesTheRow
                      ? "Slet kommentaren"
                      : "Fjern video",
                    removeBusy: removing,
                  }))}
                  onImageClick={setLightboxIndex}
                  categoryId={groupId}
                  className="text-content text-foreground mt-0.5 break-words [&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_a:not(.mention)]:text-indigo-600 dark:[&_a:not(.mention)]:text-indigo-400 [&_a:not(.mention)]:underline hover:[&_a:not(.mention)]:text-indigo-700 dark:hover:[&_a:not(.mention)]:text-indigo-300 [&_.youtube-embed]:w-[min(100%,360px)] [&_.mention]:text-indigo-700 dark:[&_.mention]:text-indigo-300 [&_.mention]:bg-indigo-50 dark:[&_.mention]:bg-indigo-950 [&_.mention]:px-1 [&_.mention]:py-0.5 [&_.mention]:rounded [&_.mention]:font-medium [&_.mention]:no-underline hover:[&_.mention]:bg-indigo-100 dark:hover:[&_.mention]:bg-indigo-900"
                />
              )}
              {/* Audio and files come off `attachments`, not out of the body:
                  the composer sends rows and writes no token for
                  `RichContent`'s strip pass to find (§1.12), so an MP3 or a
                  PDF was submitted and then drawn by nobody. Withheld while
                  editing, like the body it hangs off — `CommentEditor` draws
                  the same rows in its own tray. */}
              {!isEditing && <AttachedFiles attachments={comment.attachments} />}
            </div>
            {/*
             * OSO-585 — ABSOLUTE, NOT A FLEX SIBLING.
             *
             * As a sibling the button took its ~30 px plus the gap off the
             * bubble in EVERY comment, whether or not it was on screen:
             * `menuRevealClass` only fades it, and only under `pointer-fine:lg:`.
             * Hung off the wrapper instead — already `relative`, and already what
             * the dropdown below is positioned against — the bubble gets the whole
             * column back, and the only width still spent on the button is the
             * `pr-5` on the author line.
             */}
            {hasMenu && !isEditing && (
              <ActionMenu
                label={`Handlinger for kommentar fra ${authorName}`}
                size="sm"
                open={showMenu}
                onOpenChange={setShowMenu}
                className={`absolute right-0 top-0 z-10 transition-opacity ${menuRevealClass}`}
              >
                {canEdit && (
                  <ActionMenuItem onSelect={handleStartEdit} disabled={editLoading}>
                    Rediger
                  </ActionMenuItem>
                )}
                {canHide && <ActionMenuItem onSelect={handleHide}>Skjul</ActionMenuItem>}
                {canUnhide && <ActionMenuItem onSelect={handleUnhide}>Vis igen</ActionMenuItem>}
                {canDelete && (
                  <ActionMenuItem onSelect={handleDelete} destructive>
                    Slet
                  </ActionMenuItem>
                )}
              </ActionMenu>
            )}
            <RemoveContentDialog
              action={removal}
              kind="comment"
              moderating={moderatingRemoval}
              rules={flagRules}
              rulesLoading={rulesLoading}
              ruleId={removalRuleId}
              setRuleId={setRemovalRuleId}
              pending={removalPending}
              error={actionError}
              onConfirm={() => void confirmRemoval()}
              onCancel={cancelRemoval}
            />
            {/* The chip keeps its hairline and loses its shadow (OSO-584 §3):
                it straddles the bubble's edge, so on `bg-muted` the fill alone
                separates it but on the card's `bg-surface` nothing would — the
                border is the half that is load-bearing, the shadow was a second
                separation doing the same job. */}
            {!pending && hasReactions && (
              <div className="absolute -bottom-2 right-2 bg-surface rounded-full border border-border px-1.5 py-0.5">
                <ReactionSummary counts={counts} postId={comment.id} />
              </div>
            )}
          </div>
          {/* While the removal dialog is open, IT shows the error. */}
          {actionError && !isEditing && !showFlagForm && !removal && (
            <p className="ml-3 mt-1 text-xs text-destructive">{actionError}</p>
          )}
          {removeError && <p className="ml-3 mt-1 text-xs text-destructive">{removeError}</p>}
          <div className="flex items-center gap-3 ml-3 mt-1">
            <TimeAgo date={comment.createdAt} />
            <EditedLabel at={comment.editedAt} />
            {pending && (
              <span
                className={`text-xs ${
                  blockedByFailure ? "text-destructive" : "text-subtle-foreground"
                }`}
              >
                {!blockedByFailure
                  ? "Udgives, når videoen er klar"
                  : emptiesTheRow
                    ? "Videoen kunne ikke behandles, og kommentaren er kun den."
                    : "Videoen kunne ikke behandles. Fjern den, så bliver kommentaren slået op."}
              </span>
            )}
            {comment.isHidden && <HiddenChip />}
            {!pending && canReact && (
              <EmojiReactionPicker
                groupId={groupId}
                onSelect={(emoji, customEmojiUrl) => {
                  enterThread(postId);
                  // BOTH arguments: the second is the custom emoji's image,
                  // and `usePostReaction` draws the optimistic chip from it.
                  // Dropping it rendered the bare shortcode until the server
                  // answered.
                  handleReact(emoji, customEmojiUrl);
                }}
                // A one-click like is the commonest way of reacting at all —
                // the picker only opens on hover — so it has to enter the
                // thread too, or the predicate would exclude its own example.
                onQuickLike={() => {
                  enterThread(postId);
                  handleQuickLike();
                }}
                hoverMode
              >
                <button
                  className={`text-xs font-semibold transition-colors py-1 ${
                    currentReaction
                      ? "text-primary"
                      : "text-muted-foreground hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
                >
                  {currentReaction ? (
                    <span className="flex items-center gap-1">
                      <Emoji
                        emoji={currentReaction}
                        url={currentReactionUrl}
                        size="sm"
                      />
                      Reageret
                    </span>
                  ) : (
                    "Synes godt om"
                  )}
                </button>
              </EmojiReactionPicker>
            )}
            {!pending && canReply && (
              <button
                onClick={() => {
                  enterThread(postId);
                  setShowReply((v) => !v);
                }}
                className="text-xs text-muted-foreground hover:text-gray-700 dark:hover:text-gray-300 font-semibold transition-colors py-1"
              >
                Svar
              </button>
            )}
            {canFlag &&
              (flagDone ? (
                <span className="text-xs text-subtle-foreground py-1">Anmeldt ✓</span>
              ) : (
                <button
                  onClick={() => setShowFlagForm((v) => !v)}
                  className="text-xs text-muted-foreground hover:text-gray-700 dark:hover:text-gray-300 font-semibold transition-colors py-1"
                >
                  Anmeld
                </button>
              ))}
          </div>
          {showFlagForm && (
            <div className="mt-2 space-y-1.5">
              <FlagRuleSelect rules={flagRules} value={flagRuleId} onChange={setFlagRuleId} />
              <textarea
                value={flagReason}
                onChange={(e) => setFlagReason(e.target.value)}
                rows={2}
                placeholder="Hvad er problemet? (valgfrit)"
                className="w-full rounded-lg border border-border-strong bg-surface text-foreground px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              />
              {actionError && <p className="text-xs text-destructive">{actionError}</p>}
              <div className="flex gap-2">
                <button
                  onClick={flag}
                  disabled={flagLoading}
                  className="px-2.5 py-1 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {flagLoading ? "Sender..." : "Send"}
                </button>
                <button
                  onClick={() => setShowFlagForm(false)}
                  className="px-2.5 py-1 text-xs font-medium text-muted-foreground bg-muted rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Annuller
                </button>
              </div>
            </div>
          )}
          {showReply && (
            <div className="mt-2">
              {/* THE PARENT IS THE URL: a reply is a comment posted to THIS
                  comment's id, so there is no reply_to_post_number to carry. */}
              <CommentComposer
                parentId={comment.id}
                groupSlug={groupSlug}
                postId={postId}
                replyTo={replyTag}
                groupId={groupId}
                currentUserId={currentUserId}
                onCancel={() => setShowReply(false)}
                onSuccess={() => setShowReply(false)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Pinned top reply, always visible (Mode C) */}
      {pinnedReplies && pinnedReplies.length > 0 && (
        <div className="ml-10 mt-1 space-y-2">
          {pinnedReplies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              postId={postId}
              groupId={groupId}
              groupSlug={groupSlug}
              currentUserId={currentUserId}
              nested
              unreadIds={unreadIds}
            />
          ))}
        </div>
      )}

      {/* Reply count toggle + nested replies */}
      {replyCount > 0 && (
        <div className="ml-10 mt-1">
          {!showReplies ? (
            (pinnedReplyIds.size === 0 || (remainingReplies?.length ?? replyCount) > 0) && (
              <button
                onClick={() => {
                  enterThread(postId);
                  setShowReplies(true);
                }}
                className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors py-1"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
                {pinnedReplyIds.size > 0
                  ? (remainingReplies?.length ?? Math.max(replyCount - pinnedReplyIds.size, 0))
                  : replyCount}{" "}
                svar
              </button>
            )
          ) : (
            <>
              <button
                onClick={() => setShowReplies(false)}
                className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors py-1"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
                Skjul svar
              </button>
              {/* No loading state to draw: the branch is already in the
                  thread this card renders, so expanding it fetches nothing. */}
              <div className="space-y-2 mt-1">
                {(pinnedReplyIds.size > 0 ? remainingReplies : displayReplies)?.map((reply) => (
                  <CommentItem
                    key={reply.id}
                    comment={reply}
                    postId={postId}
                    groupId={groupId}
                    groupSlug={groupSlug}
                    currentUserId={currentUserId}
                    nested
                    unreadIds={unreadIds}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Image lightbox — the shared one, with the comment itself as sidebar */}
      {lightboxIndex !== null && images.length > 0 && (
        <ImageLightbox
          items={images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          sidebar={<CommentLightboxSidebar comment={comment} bodyHtml={bodyHtml} />}
        />
      )}
    </div>
  );
}
